import ConditionalRenderer from '@/components/ui/custom/ConditionalRenderer';
import useInfiniteScroll from '@/data/hook/useInfiniteScroll';
import { fadeIn } from '@/utils/animations/commom';
import { scrollBarYClassName } from '@/utils/scrollBarClassName';
import { motion } from 'motion/react';
import {
  AriaRole,
  PropsWithChildren,
  UIEventHandler,
  forwardRef,
  useImperativeHandle,
  useState,
} from 'react';
import { twMerge } from 'tailwind-merge';

import { Loading } from '@/components/ui/daisy/loading';

export type InfinityListOptions = {
  onReachEnd?(): unknown | Promise<unknown>;
  onReachStart?(): unknown | Promise<unknown>;
  isFetchingNextPage?: boolean;
  options?: IntersectionObserverInit;
  isFetchingPreviousPage?: boolean;
  hasNextPage?: boolean;
  hasPreviousPage?: boolean;
  className?: string;
  scroll?: boolean;
  onScroll?: UIEventHandler<HTMLUListElement>;
  role?: AriaRole;
};

export type InfinityListProps = PropsWithChildren<InfinityListOptions>;

const InfinityList = forwardRef<HTMLUListElement, InfinityListProps>(
  (
    {
      children,
      onReachStart,
      onReachEnd,
      isFetchingPreviousPage,
      isFetchingNextPage,
      className,
      hasNextPage,
      hasPreviousPage,
      options,
      scroll,
      onScroll,
      role,
    }: InfinityListProps,
    listRef,
  ) => {
    const [localListRef, setLocalListRef] = useState<HTMLUListElement | null>(
      null,
    );

    const { startSentinelRef, endSentinelRef } = useInfiniteScroll({
      onReachEnd,
      onReachStart,
      hasNextPage,
      hasPreviousPage,
      scroll,
      options,
      scrollContainer: localListRef,
    });

    useImperativeHandle(listRef, () => localListRef as HTMLUListElement);

    const listClassName = scroll ? scrollBarYClassName : '';

    return (
      <motion.ul
        role={role}
        onScroll={onScroll}
        ref={setLocalListRef}
        className={twMerge(
          'flex flex-col w-full h-full',
          listClassName,
          className,
        )}
        {...fadeIn}
      >
        <ConditionalRenderer condition={hasPreviousPage}>
          <div
            role="presentation"
            data-positiontarget="start"
            ref={startSentinelRef}
          >
            <ConditionalRenderer condition={isFetchingPreviousPage}>
              <Loading className="m-2" color="primary" size="sm" />
            </ConditionalRenderer>
          </div>
        </ConditionalRenderer>

        {children}

        <ConditionalRenderer condition={hasNextPage}>
          <div
            role="presentation"
            data-positiontarget="end"
            ref={endSentinelRef}
            className="flex w-full justify-center col-span-full"
          >
            <ConditionalRenderer condition={isFetchingNextPage}>
              <Loading className="m-2" color="primary" size="sm" />
            </ConditionalRenderer>
          </div>
        </ConditionalRenderer>
      </motion.ul>
    );
  },
);

export default InfinityList;
